import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  adminEnquiries, setEnquiryStatus, deleteEnquiry,
  adminOrders, setOrderStatus, deleteOrder,
  getMenu, getCategories, getGallery, getReels, getCareers,
  listApplications, updateApplicationStatus, deleteApplication,
  createContent, updateContent, deleteContent, resolveImg, changePassword,
  listStaff, createStaff, resetStaffPassword, deleteStaff,
} from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ImageField from '../components/ImageField';
import { useToast } from '../hooks/use-toast';
import { BRAND } from '../data/mock';
import { LogOut, Plus, Pencil, Trash2, Mail, ShoppingBag, UtensilsCrossed, Tags, Images, Clapperboard, ExternalLink, Bell, TrendingUp, IndianRupee, Wallet, KeyRound, Users, Briefcase, FileText, Download } from 'lucide-react';

const STATUS_COLORS = { new: 'bg-orange-500', contacted: 'bg-blue-500', done: 'bg-green-600', cancelled: 'bg-red-600' };

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso); const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isStaff = (user?.role || 'owner') === 'staff';

  const [enquiries, setEnquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [categories, setCategories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reels, setReels] = useState([]);
  const [careers, setCareers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [staff, setStaff] = useState([]);

  const [dialog, setDialog] = useState(null); // { type, item }
  const [showNotif, setShowNotif] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem('zs_admin_lastseen') || '1970-01-01T00:00:00Z');

  const stats = useMemo(() => {
    const todaysOrders = orders.filter(o => isToday(o.created_at));
    const revenueToday = todaysOrders.reduce((s, o) => s + (o.total || 0), 0);
    const revenueTotal = orders.reduce((s, o) => s + (o.total || 0), 0);
    const newEnquiries = enquiries.filter(e => (e.status || 'new') === 'new').length;
    return { ordersToday: todaysOrders.length, revenueToday, ordersTotal: orders.length, revenueTotal, newEnquiries };
  }, [orders, enquiries]);

  const notifications = useMemo(() => {
    const items = [
      ...orders.map(o => ({ id: o.id, kind: 'order', when: o.created_at, label: `New order ₹${o.total} (${o.platform})`, sub: o.customer?.name || 'Guest' })),
      ...enquiries.map(e => ({ id: e.id, kind: 'enquiry', when: e.created_at, label: `New ${e.type} enquiry`, sub: e.name })),
    ].filter(x => x.when && x.when > lastSeen);
    return items.sort((a, b) => (a.when < b.when ? 1 : -1));
  }, [orders, enquiries, lastSeen]);

  const markAllRead = () => {
    const now = new Date().toISOString();
    localStorage.setItem('zs_admin_lastseen', now);
    setLastSeen(now);
    setShowNotif(false);
  };

  const loadAll = useCallback(async () => {
    try {
      if (isStaff) {
        setOrders(await adminOrders());
        return;
      }
      const [e, o, m, c, g, r, cr, ap, s] = await Promise.all([
        adminEnquiries(), adminOrders(), getMenu(), getCategories(), getGallery(), getReels(), getCareers(), listApplications(), listStaff(),
      ]);
      setEnquiries(e); setOrders(o); setMenu(m); setCategories(c); setGallery(g); setReels(r); setCareers(cr); setApplications(ap); setStaff(s);
    } catch (err) {
      toast({ title: 'Failed to load data', description: err?.response?.data?.detail || 'Retry', variant: 'destructive' });
    }
  }, [toast, isStaff]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleLogout = async () => { await logout(); navigate('/admin'); };

  const saveContent = async (name, item, body) => {
    try {
      if (item?.id) await updateContent(name, item.id, body);
      else await createContent(name, body);
      toast({ title: 'Saved' });
      setDialog(null);
      loadAll();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Retry', variant: 'destructive' });
    }
  };

  const removeContent = async (name, id) => {
    if (!window.confirm('Delete this item?')) return;
    await deleteContent(name, id);
    toast({ title: 'Deleted' });
    loadAll();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-md border-b border-orange-500/20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="logo" className="w-10 h-10 object-contain" />
            <div className="font-display text-xl tracking-wider">{isStaff ? 'STAFF' : 'ADMIN'} <span className="text-orange-500">PANEL</span></div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-white/60 hover:text-orange-400 text-xs uppercase tracking-widest inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View Site</a>
            <div className="relative">
              <button data-testid="notif-bell" onClick={() => setShowNotif(s => !s)} className="relative w-10 h-10 rounded-full bg-neutral-900 border border-white/10 hover:border-orange-500/60 flex items-center justify-center text-white/80 hover:text-orange-400 transition">
                <Bell className="w-4 h-4" />
                {notifications.length > 0 && <span data-testid="notif-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">{notifications.length}</span>}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-80 bg-neutral-950 border border-white/10 rounded-2xl shadow-2xl p-2 z-50" data-testid="notif-dropdown">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
                    <span className="font-bebas text-lg">Notifications</span>
                    {notifications.length > 0 && <button data-testid="notif-mark-read" onClick={markAllRead} className="text-xs text-orange-400 hover:text-orange-300 uppercase tracking-widest">Mark all read</button>}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 && <div className="px-3 py-8 text-center text-white/40 text-sm">You're all caught up 🎉</div>}
                    {notifications.slice(0, 20).map(n => (
                      <div key={n.kind + n.id} className="px-3 py-2 rounded-xl hover:bg-white/5">
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`w-2 h-2 rounded-full ${n.kind === 'order' ? 'bg-green-500' : 'bg-orange-500'}`} />
                          <span className="text-white/90">{n.label}</span>
                        </div>
                        <div className="text-xs text-white/45 ml-4">{n.sub} • {new Date(n.when).toLocaleString()}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <span className="text-white/50 text-sm hidden sm:block">{user?.email}</span>
            <Button data-testid="change-password-btn" onClick={() => setShowPwd(true)} variant="outline" className="h-9 border-white/15 bg-neutral-900 text-white hidden sm:inline-flex"><KeyRound className="w-4 h-4 mr-2" /> Password</Button>
            <Button data-testid="admin-logout" onClick={handleLogout} variant="outline" className="h-9 border-white/15 bg-neutral-900 text-white"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Stats strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard icon={ShoppingBag} label="Orders Today" value={stats.ordersToday} />
          <StatCard icon={IndianRupee} label="Revenue Today" value={`₹${stats.revenueToday}`} accent />
          <StatCard icon={Wallet} label="Total Revenue" value={`₹${stats.revenueTotal}`} />
          {!isStaff && <StatCard icon={TrendingUp} label="New Enquiries" value={stats.newEnquiries} />}
        </div>

        <Tabs defaultValue={isStaff ? 'orders' : 'enquiries'}>
          <TabsList className="bg-neutral-900 border border-white/10 flex-wrap h-auto">
            {!isStaff && <TabsTrigger value="enquiries" data-testid="tab-enquiries"><Mail className="w-4 h-4 mr-2" /> Enquiries <Badge className="ml-2 bg-orange-500">{enquiries.length}</Badge></TabsTrigger>}
            <TabsTrigger value="orders" data-testid="tab-orders"><ShoppingBag className="w-4 h-4 mr-2" /> Orders <Badge className="ml-2 bg-orange-500">{orders.length}</Badge></TabsTrigger>
            {!isStaff && <TabsTrigger value="menu" data-testid="tab-menu"><UtensilsCrossed className="w-4 h-4 mr-2" /> Menu <Badge className="ml-2 bg-orange-500">{menu.length}</Badge></TabsTrigger>}
            {!isStaff && <TabsTrigger value="categories" data-testid="tab-categories"><Tags className="w-4 h-4 mr-2" /> Categories</TabsTrigger>}
            {!isStaff && <TabsTrigger value="gallery" data-testid="tab-gallery"><Images className="w-4 h-4 mr-2" /> Gallery</TabsTrigger>}
            {!isStaff && <TabsTrigger value="reels" data-testid="tab-reels"><Clapperboard className="w-4 h-4 mr-2" /> Reels</TabsTrigger>}
            {!isStaff && <TabsTrigger value="careers" data-testid="tab-careers"><Briefcase className="w-4 h-4 mr-2" /> Careers <Badge className="ml-2 bg-orange-500">{careers.length}</Badge></TabsTrigger>}
            {!isStaff && <TabsTrigger value="applicants" data-testid="tab-applicants"><FileText className="w-4 h-4 mr-2" /> Applicants <Badge className="ml-2 bg-orange-500">{applications.length}</Badge></TabsTrigger>}
            {!isStaff && <TabsTrigger value="staff" data-testid="tab-staff"><Users className="w-4 h-4 mr-2" /> Staff <Badge className="ml-2 bg-orange-500">{staff.length}</Badge></TabsTrigger>}
          </TabsList>

          {isStaff && (
            <div className="mt-4 text-xs text-white/40 uppercase tracking-widest">Staff access — you can view and update orders.</div>
          )}

          {/* ENQUIRIES */}
          <TabsContent value="enquiries" className="mt-6 space-y-3">
            {enquiries.length === 0 && <Empty label="No enquiries yet" />}
            {enquiries.map(e => (
              <div key={e.id} data-testid={`enquiry-${e.id}`} className="bg-neutral-900 border border-white/10 rounded-xl p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bebas text-xl">{e.name}</span>
                      <Badge variant="outline" className="border-orange-500/40 text-orange-300 uppercase text-[10px]">{e.type}</Badge>
                      {e.payment_status === 'paid' && <Badge className="bg-green-600 text-[10px] uppercase">Fee Paid ₹{e.fee_inr}</Badge>}
                    </div>
                    <div className="text-sm text-white/60 mt-1">{e.email} • {e.phone || '—'}</div>
                    <div className="text-sm text-white/50 mt-1">{e.country} {e.city && `• ${e.city}`} {e.budget && `• Budget: ${e.budget}`}</div>
                    {e.message && <div className="text-sm text-white/70 mt-2 max-w-2xl">{e.message}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusSelect value={e.status} onChange={async (s) => { await setEnquiryStatus(e.id, s); loadAll(); }} />
                    <button onClick={async () => { if (window.confirm('Delete enquiry?')) { await deleteEnquiry(e.id); loadAll(); } }} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* ORDERS */}
          <TabsContent value="orders" className="mt-6 space-y-3">
            {orders.length === 0 && <Empty label="No orders yet" />}
            {orders.map(o => (
              <div key={o.id} data-testid={`order-${o.id}`} className="bg-neutral-900 border border-white/10 rounded-xl p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bebas text-xl text-orange-500">₹{o.total}</span>
                      <Badge variant="outline" className="border-white/20 uppercase text-[10px]">{o.platform}</Badge>
                    </div>
                    <div className="text-sm text-white/60 mt-1">{o.customer?.name || 'Guest'} • {o.customer?.phone || '—'}</div>
                    <div className="text-sm text-white/70 mt-2">{o.items.map(it => `${it.name} ×${it.qty}`).join(', ')}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusSelect value={o.status} onChange={async (s) => { await setOrderStatus(o.id, s); loadAll(); }} />
                    <button onClick={async () => { if (window.confirm('Delete order?')) { await deleteOrder(o.id); loadAll(); } }} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* MENU */}
          <TabsContent value="menu" className="mt-6">
            <SectionHeader title="Menu Items" onAdd={() => setDialog({ type: 'menu', item: null })} testid="add-menu" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {menu.map(m => (
                <div key={m.id} data-testid={`menu-item-${m.id}`} className="bg-neutral-900 border border-white/10 rounded-xl overflow-hidden">
                  <img src={resolveImg(m.image_url)} alt={m.name} className="w-full h-32 object-cover" />
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="font-bebas text-lg">{m.name}</span>
                      <span className="text-orange-500 font-bold">₹{m.price}</span>
                    </div>
                    <div className="text-xs text-white/50 mt-1">{m.category} {m.is_bestseller && <Badge className="ml-1 bg-orange-500 text-[9px]">BESTSELLER</Badge>} {m.is_new && <Badge className="ml-1 bg-green-600 text-[9px]">NEW</Badge>}</div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'menu', item: m })} className="flex-1 border-white/15 bg-neutral-950"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => removeContent('menu', m.id)} className="border-red-500/30 text-red-400 bg-neutral-950"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* CATEGORIES */}
          <TabsContent value="categories" className="mt-6">
            <SectionHeader title="Categories" onAdd={() => setDialog({ type: 'categories', item: null })} testid="add-category" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              {categories.map(c => (
                <div key={c.id} data-testid={`category-${c.id}`} className="bg-neutral-900 border border-white/10 rounded-xl p-4 flex items-center justify-between">
                  <span className="font-bebas text-lg">{c.name}</span>
                  <div className="flex gap-2">
                    <button onClick={() => setDialog({ type: 'categories', item: c })} className="text-white/50 hover:text-orange-400"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => removeContent('categories', c.id)} className="text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="mt-6">
            <SectionHeader title="Gallery Images" onAdd={() => setDialog({ type: 'gallery', item: null })} testid="add-gallery" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              {gallery.map(g => (
                <div key={g.id} data-testid={`gallery-${g.id}`} className="relative group rounded-xl overflow-hidden border border-white/10">
                  <img src={resolveImg(g.image_url)} alt={g.caption} className="w-full h-40 object-cover" />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'gallery', item: g })} className="border-white/15 bg-neutral-900"><Pencil className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => removeContent('gallery', g.id)} className="border-red-500/30 text-red-400 bg-neutral-900"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* REELS */}
          <TabsContent value="reels" className="mt-6">
            <SectionHeader title="Reels" onAdd={() => setDialog({ type: 'reels', item: null })} testid="add-reel" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {reels.map(r => (
                <div key={r.id} data-testid={`reel-${r.id}`} className="bg-neutral-900 border border-white/10 rounded-xl overflow-hidden">
                  <img src={resolveImg(r.image_url)} alt={r.title} className="w-full h-40 object-cover" />
                  <div className="p-4">
                    <div className="font-bebas text-lg">{r.title}</div>
                    <div className="text-xs text-orange-400">{r.tag} • {r.likes} likes</div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'reels', item: r })} className="flex-1 border-white/15 bg-neutral-950"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => removeContent('reels', r.id)} className="border-red-500/30 text-red-400 bg-neutral-950"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* CAREERS */}
          <TabsContent value="careers" className="mt-6">
            <SectionHeader title="Job Openings" onAdd={() => setDialog({ type: 'careers', item: null })} testid="add-career" />
            <div className="space-y-3 mt-4">
              {careers.length === 0 && <Empty label="No job openings yet" />}
              {careers.map(job => (
                <div key={job.id} data-testid={`admin-career-${job.id}`} className="bg-neutral-900 border border-white/10 rounded-xl p-4 flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bebas text-xl">{job.title}</span>
                      <Badge className={job.is_open ? 'bg-green-600 text-[10px]' : 'bg-neutral-600 text-[10px]'}>{job.is_open ? 'OPEN' : 'CLOSED'}</Badge>
                    </div>
                    <div className="text-sm text-white/55 mt-1">{job.department && `${job.department} • `}{job.type} • {job.location}{job.salary && ` • ${job.salary}`}</div>
                    {job.description && <div className="text-sm text-white/70 mt-2 max-w-2xl">{job.description}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDialog({ type: 'careers', item: job })} className="border-white/15 bg-neutral-950"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => removeContent('careers', job.id)} className="border-red-500/30 text-red-400 bg-neutral-950"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* APPLICANTS */}
          <TabsContent value="applicants" className="mt-6">
            <div className="space-y-3">
              {applications.length === 0 && <Empty label="No job applications yet" />}
              {applications.map(a => (
                <div key={a.id} data-testid={`applicant-${a.id}`} className="bg-neutral-900 border border-white/10 rounded-xl p-4 flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bebas text-xl">{a.name}</span>
                      {a.position && <Badge variant="outline" className="border-orange-500/40 text-orange-300 uppercase text-[10px]">{a.position}</Badge>}
                    </div>
                    <div className="text-sm text-white/55 mt-1">{a.email}{a.phone && ` • ${a.phone}`}</div>
                    {a.message && <div className="text-sm text-white/70 mt-2 max-w-2xl">{a.message}</div>}
                    <div className="text-xs text-white/35 mt-2">{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.cv_url
                      ? <a href={resolveImg(a.cv_url)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-orange-500/40 text-orange-300 bg-neutral-950"><Download className="w-3 h-3 mr-1" /> CV</Button></a>
                      : <span className="text-xs text-white/30">No CV</span>}
                    <Select value={a.status} onValueChange={async (v) => { await updateApplicationStatus(a.id, v); loadAll(); }}>
                      <SelectTrigger className="h-9 w-36 bg-neutral-950 border-white/10 text-sm" data-testid={`applicant-status-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="reviewed">Reviewed</SelectItem>
                        <SelectItem value="shortlisted">Shortlisted</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={async () => { if (window.confirm('Delete this application?')) { await deleteApplication(a.id); loadAll(); } }} className="border-red-500/30 text-red-400 bg-neutral-950"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* STAFF */}
          {!isStaff && (
            <TabsContent value="staff" className="mt-6">
              <StaffManager staff={staff} reload={loadAll} toast={toast} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Edit dialogs */}
      {dialog && (
        <EditDialog
          dialog={dialog}
          categories={categories}
          onClose={() => setDialog(null)}
          onSave={saveContent}
        />
      )}

      {/* Change password dialog */}
      {showPwd && <ChangePasswordDialog onClose={() => setShowPwd(false)} toast={toast} />}
    </div>
  );
}

function StaffManager({ staff, reload, toast }) {
  const [form, setForm] = useState({ username: '', name: '', password: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const add = async () => {
    setBusy(true);
    try {
      await createStaff(form);
      toast({ title: 'Staff added', description: `${form.username} can now log in.` });
      setForm({ username: '', name: '', password: '' });
      reload();
    } catch (err) {
      toast({ title: 'Could not add staff', description: err?.response?.data?.detail || 'Retry', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove staff "${s.username}"?`)) return;
    await deleteStaff(s.user_id);
    toast({ title: 'Staff removed' });
    reload();
  };

  const resetPwd = async (s) => {
    const np = window.prompt(`New password for "${s.username}" (min 6 chars):`);
    if (!np) return;
    try {
      await resetStaffPassword(s.user_id, np);
      toast({ title: 'Password reset', description: `Share the new password with ${s.username}.` });
    } catch (err) {
      toast({ title: 'Reset failed', description: err?.response?.data?.detail || 'Retry', variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6">
        <h2 className="font-display text-2xl tracking-wide">Add Kitchen Staff</h2>
        <p className="text-sm text-white/50 mt-1">Staff can view and update orders only — they can't edit your menu, content, enquiries or settings.</p>
        <div className="grid sm:grid-cols-3 gap-3 mt-5">
          <Input data-testid="staff-username" placeholder="Username" value={form.username} onChange={e => set('username', e.target.value)} className="h-11 bg-black/50 border-white/10" />
          <Input data-testid="staff-name" placeholder="Display name" value={form.name} onChange={e => set('name', e.target.value)} className="h-11 bg-black/50 border-white/10" />
          <Input data-testid="staff-password" type="password" placeholder="Password (min 6)" value={form.password} onChange={e => set('password', e.target.value)} className="h-11 bg-black/50 border-white/10" />
        </div>
        <Button data-testid="add-staff-submit" onClick={add} disabled={busy} className="mt-4 h-11 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><Plus className="w-4 h-4 mr-1" /> {busy ? 'Adding…' : 'Add Staff'}</Button>
      </div>

      <div className="mt-6 space-y-3">
        {staff.length === 0 && <Empty label="No staff accounts yet" />}
        {staff.map(s => (
          <div key={s.user_id} data-testid={`staff-${s.user_id}`} className="bg-neutral-900 border border-white/10 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-full bg-orange-500/15 border border-orange-500/40 flex items-center justify-center text-orange-400"><Users className="w-5 h-5" /></span>
              <div>
                <div className="font-bebas text-lg">{s.name || s.username}</div>
                <div className="text-xs text-white/50">@{s.username} · staff</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => resetPwd(s)} className="border-white/15 bg-neutral-950"><KeyRound className="w-3 h-3 mr-1" /> Reset</Button>
              <Button size="sm" variant="outline" onClick={() => remove(s)} className="border-red-500/30 text-red-400 bg-neutral-950"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChangePasswordDialog({ onClose, toast }) {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setError('');
    if (form.new_password.length < 6) { setError('New password must be at least 6 characters.'); return; }
    if (form.new_password !== form.confirm) { setError('New passwords do not match.'); return; }
    setBusy(true);
    try {
      await changePassword(form.current_password, form.new_password);
      toast({ title: 'Password changed', description: 'Use your new password next time you sign in.' });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not change password.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border border-orange-500/30 text-white max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl tracking-wide">Change Password</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <Input data-testid="current-password" type="password" placeholder="Current password" value={form.current_password} onChange={e => set('current_password', e.target.value)} className="h-11 bg-black/50 border-white/10" />
          <Input data-testid="new-password" type="password" placeholder="New password" value={form.new_password} onChange={e => set('new_password', e.target.value)} className="h-11 bg-black/50 border-white/10" />
          <Input data-testid="confirm-password" type="password" placeholder="Confirm new password" value={form.confirm} onChange={e => set('confirm', e.target.value)} className="h-11 bg-black/50 border-white/10" />
          {error && <p data-testid="change-password-error" className="text-red-400 text-sm">{error}</p>}
          <Button data-testid="save-password" onClick={submit} disabled={busy} className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">{busy ? 'Saving…' : 'Update Password'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Empty({ label }) {
  return <div className="text-center py-16 text-white/40 uppercase tracking-widest text-sm">{label}</div>;
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className={`food-card rounded-2xl border p-5 will-change-transform ${accent ? 'bg-gradient-to-br from-orange-500/20 to-orange-700/10 border-orange-500/40' : 'bg-neutral-900 border-white/10'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-white/50">{label}</span>
        <Icon className="w-4 h-4 text-orange-500" />
      </div>
      <div className="mt-2 font-display text-3xl md:text-4xl text-white">{value}</div>
    </div>
  );
}

function SectionHeader({ title, onAdd, testid }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="font-display text-2xl tracking-wide">{title}</h2>
      <Button data-testid={testid} onClick={onAdd} className="bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><Plus className="w-4 h-4 mr-1" /> Add</Button>
    </div>
  );
}

function StatusSelect({ value, onChange }) {
  return (
    <Select value={value || 'new'} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-36 bg-black/50 border-white/10" data-testid="status-select">
        <span className={`w-2 h-2 rounded-full mr-2 ${STATUS_COLORS[value] || 'bg-orange-500'}`} />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="new">New</SelectItem>
        <SelectItem value="contacted">Contacted</SelectItem>
        <SelectItem value="done">Done</SelectItem>
        <SelectItem value="cancelled">Cancelled</SelectItem>
      </SelectContent>
    </Select>
  );
}

function EditDialog({ dialog, categories, onClose, onSave }) {
  const { type, item } = dialog;
  const [form, setForm] = useState(() => {
    if (type === 'menu') return { category: item?.category || categories[0]?.name || '', name: item?.name || '', price: item?.price || '', desc: item?.desc || '', image_url: item?.image_url || '', is_bestseller: item?.is_bestseller || false, is_new: item?.is_new || false, order: item?.order || 0 };
    if (type === 'categories') return { name: item?.name || '', order: item?.order || 0 };
    if (type === 'gallery') return { image_url: item?.image_url || '', caption: item?.caption || '', order: item?.order || 0 };
    if (type === 'reels') return { title: item?.title || '', tag: item?.tag || '', likes: item?.likes || '0', image_url: item?.image_url || '', video_url: item?.video_url || '', order: item?.order || 0 };
    if (type === 'careers') return { title: item?.title || '', department: item?.department || '', location: item?.location || 'Rajajipuram, Lucknow', type_emp: item?.type || 'Full-time', salary: item?.salary || '', description: item?.description || '', is_open: item?.is_open ?? true, order: item?.order || 0 };
    return {};
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    const body = { ...form };
    if (type === 'menu') body.price = parseFloat(body.price) || 0;
    if (type === 'careers') { body.type = body.type_emp; delete body.type_emp; }
    onSave(type, item, body);
  };

  const titles = { menu: 'Menu Item', categories: 'Category', gallery: 'Gallery Image', reels: 'Reel', careers: 'Job Opening' };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-neutral-950 border border-orange-500/30 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl tracking-wide">{item ? 'Edit' : 'Add'} {titles[type]}</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          {type === 'menu' && (
            <>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger className="h-11 bg-black/50 border-white/10" data-testid="menu-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="menu-name" placeholder="Item name" value={form.name} onChange={e => set('name', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <Input data-testid="menu-price" placeholder="Price (₹)" type="number" value={form.price} onChange={e => set('price', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <Textarea placeholder="Description" value={form.desc} onChange={e => set('desc', e.target.value)} className="bg-black/50 border-white/10" />
              <ImageField value={form.image_url} onChange={(v) => set('image_url', v)} />
              <label className="flex items-center gap-2 text-sm text-white/70"><Checkbox checked={form.is_bestseller} onCheckedChange={(v) => set('is_bestseller', !!v)} className="border-orange-500 data-[state=checked]:bg-orange-500" /> Mark as Bestseller (shows on Home)</label>
              <label className="flex items-center gap-2 text-sm text-white/70"><Checkbox data-testid="menu-is-new" checked={form.is_new} onCheckedChange={(v) => set('is_new', !!v)} className="border-orange-500 data-[state=checked]:bg-orange-500" /> Mark as New (shows a "NEW" badge on the menu)</label>
            </>
          )}
          {type === 'categories' && (
            <>
              <Input data-testid="category-name" placeholder="Category name" value={form.name} onChange={e => set('name', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <Input placeholder="Sort order" type="number" value={form.order} onChange={e => set('order', parseInt(e.target.value) || 0)} className="h-11 bg-black/50 border-white/10" />
            </>
          )}
          {type === 'gallery' && (
            <>
              <ImageField value={form.image_url} onChange={(v) => set('image_url', v)} />
              <Input placeholder="Caption" value={form.caption} onChange={e => set('caption', e.target.value)} className="h-11 bg-black/50 border-white/10" />
            </>
          )}
          {type === 'reels' && (
            <>
              <Input data-testid="reel-title" placeholder="Title" value={form.title} onChange={e => set('title', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <Input placeholder="Tag (e.g. #burgergoals)" value={form.tag} onChange={e => set('tag', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <Input placeholder="Likes (e.g. 12.4k)" value={form.likes} onChange={e => set('likes', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <ImageField value={form.image_url} onChange={(v) => set('image_url', v)} label="Thumbnail" />
              <Input placeholder="Instagram reel / video URL (optional)" value={form.video_url} onChange={e => set('video_url', e.target.value)} className="h-11 bg-black/50 border-white/10" />
            </>
          )}
          {type === 'careers' && (
            <>
              <Input data-testid="career-title" placeholder="Job title (e.g. Kitchen Chef)" value={form.title} onChange={e => set('title', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Department (e.g. Kitchen)" value={form.department} onChange={e => set('department', e.target.value)} className="h-11 bg-black/50 border-white/10" />
                <Select value={form.type_emp} onValueChange={(v) => set('type_emp', v)}>
                  <SelectTrigger className="h-11 bg-black/50 border-white/10" data-testid="career-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Full-time">Full-time</SelectItem>
                    <SelectItem value="Part-time">Part-time</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Internship">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input placeholder="Location" value={form.location} onChange={e => set('location', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <Input placeholder="Salary (e.g. ₹15,000 – ₹25,000 / month)" value={form.salary} onChange={e => set('salary', e.target.value)} className="h-11 bg-black/50 border-white/10" />
              <Textarea placeholder="Role description & requirements" value={form.description} onChange={e => set('description', e.target.value)} className="bg-black/50 border-white/10" />
              <label className="flex items-center gap-2 text-sm text-white/70"><Checkbox data-testid="career-is-open" checked={form.is_open} onCheckedChange={(v) => set('is_open', !!v)} className="border-orange-500 data-[state=checked]:bg-orange-500" /> Open for applications (visible on the Careers page)</label>
            </>
          )}
          <Button data-testid="save-content" onClick={submit} className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
